import React from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex flex-col">
      {/* Header */}
      <header className="max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-6 w-full">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 group">
            <img 
              src="/logo-icon.png" 
              alt="SiteScope" 
              className="w-12 h-12 sm:w-12 sm:h-12 md:w-14 md:h-14 transition-transform group-hover:scale-105" 
            />
            <span className="text-2xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">
              SiteScope
            </span>
          </Link>
          
          <Link 
            to="/" 
            className="text-sm sm:text-base font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
          >
            ← Back to Home
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-16 w-full">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 sm:p-10 shadow-lg border border-gray-200 dark:border-gray-700">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-6">
            Privacy Policy
          </h1>
          
          <div className="prose prose-blue dark:prose-invert max-w-none text-gray-700 dark:text-gray-300">
            <p className="text-lg">
              At SiteScope, we take your privacy seriously. This policy describes how we collect, use, and protect your personal information.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-8 mb-4">1. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-6 mb-3">Account Information</h3>
            <p>When you create an account, we collect:</p>
            <ul className="space-y-2">
              <li>Email address</li>
              <li>Username</li>
              <li>Password (encrypted)</li>
              <li>Contact number (optional)</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-6 mb-3">Website Monitoring Data</h3>
            <ul className="space-y-2">
              <li>URLs of websites you monitor</li>
              <li>Uptime and downtime logs</li>
              <li>Response times and status codes</li>
              <li>SSL certificate information</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-6 mb-3">Usage Information</h3>
            <ul className="space-y-2">
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Device information</li>
              <li>Pages visited and features used</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-8 mb-4">2. How We Use Your Information</h2>
            <p>We use your information to:</p>
            <ul className="space-y-2">
              <li><strong>Provide our service:</strong> Monitor your websites and send alerts</li>
              <li><strong>Improve our service:</strong> Analyze usage patterns and fix bugs</li>
              <li><strong>Communicate with you:</strong> Send important updates and support responses</li>
              <li><strong>Process payments:</strong> Handle subscriptions and billing</li>
              <li><strong>Ensure security:</strong> Detect and prevent fraud or abuse</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-8 mb-4">3. Data Sharing and Disclosure</h2>
            <p>We <strong>never sell your data</strong> to third parties. We only share data when:</p>
            <ul className="space-y-2">
              <li><strong>Required by law:</strong> Court orders, legal processes</li>
              <li><strong>With your consent:</strong> When you explicitly authorize sharing</li>
              <li><strong>Service providers:</strong> PayMongo for payments, Railway for hosting (under strict contracts)</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-8 mb-4">4. Data Storage and Security</h2>
            <ul className="space-y-2">
              <li><strong>Encryption:</strong> All data transmitted uses SSL/TLS encryption</li>
              <li><strong>Secure storage:</strong> Data stored on Railway's cloud infrastructure</li>
              <li><strong>Password security:</strong> Passwords are hashed using bcrypt</li>
              <li><strong>Backups:</strong> Daily automated backups for data recovery</li>
              <li><strong>Access controls:</strong> Limited employee access to user data</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-8 mb-4">5. Cookies and Tracking</h2>
            <p>We use cookies for:</p>
            <ul className="space-y-2">
              <li><strong>Authentication:</strong> Keep you logged in</li>
              <li><strong>Preferences:</strong> Remember your settings (theme, language)</li>
              <li><strong>Analytics:</strong> Understand how our service is used</li>
            </ul>
            <p className="mt-4">
              You can control cookies through your browser settings. However, disabling cookies may affect functionality.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-8 mb-4">6. Your Rights</h2>
            <p>You have the right to:</p>
            <ul className="space-y-2">
              <li><strong>Access:</strong> Request a copy of your data</li>
              <li><strong>Correction:</strong> Update inaccurate information</li>
              <li><strong>Deletion:</strong> Delete your account and data</li>
              <li><strong>Export:</strong> Download your data in JSON format</li>
              <li><strong>Opt-out:</strong> Unsubscribe from marketing emails</li>
            </ul>
            <p className="mt-4">
              To exercise these rights, email us at{' '}
              <a href="mailto:privacy@sitescope.work" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
                privacy@sitescope.work
              </a>
            </p>

            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-8 mb-4">7. Data Retention</h2>
            <ul className="space-y-2">
              <li><strong>Active accounts:</strong> Data retained while account is active</li>
              <li><strong>Deleted accounts:</strong> Data permanently deleted within 30 days</li>
              <li><strong>Monitoring logs:</strong> Retained based on your plan (30-365 days)</li>
              <li><strong>Backups:</strong> Deleted data may remain in backups for up to 90 days</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-8 mb-4">8. Children's Privacy</h2>
            <p>
              Our service is not intended for children under 18. We do not knowingly collect information from children. 
              If you believe we have collected data from a child, please contact us immediately.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-8 mb-4">9. International Data Transfers</h2>
            <p>
              Your data may be transferred to and processed in countries outside the Philippines, including the United States 
              (Railway hosting). We ensure appropriate safeguards are in place for international transfers.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-8 mb-4">10. Changes to This Policy</h2>
            <p>
              We may update this privacy policy from time to time. We'll notify you of significant changes via email 
              or through the service. Your continued use after changes constitutes acceptance.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-8 mb-4">11. Contact Us</h2>
            <p>
              For privacy concerns or questions, contact us at:
            </p>
            <ul className="space-y-2 mt-4">
              <li>
                <strong>Email:</strong>{' '}
                <a href="mailto:privacy@sitescope.work" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
                  privacy@sitescope.work
                </a>
              </li>
              <li>
                <strong>Support:</strong>{' '}
                <a href="mailto:support@sitescope.work" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
                  support@sitescope.work
                </a>
              </li>
            </ul>

            <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-6 rounded-r-lg mt-8">
              <h3 className="text-xl font-bold text-blue-900 dark:text-blue-100 mt-0 mb-3">
                🔒 Your Privacy Matters
              </h3>
              <p className="text-blue-800 dark:text-blue-200 mb-0">
                We're committed to protecting your privacy. We never sell your data, and you have full control 
                over your information. If you have any concerns, we're here to help.
              </p>
            </div>

            <div className="bg-gray-100 dark:bg-gray-700/50 p-6 rounded-lg mt-8">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-0">
                <strong>Last updated:</strong> November 7, 2025
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
